/**
 * Prepares a scraped `surveyjs_config` for rendering.
 *
 * Outside SurveyRenderer.tsx so fast refresh keeps working there and this stays
 * unit-testable (see problem-text.ts for the same reasoning).
 */

import { resolveImageSrcsInJson } from "./types";
import { normalizeImages, type ImageRef } from "./media";

/**
 * Resolve image URLs in the config, repairing tags whose src the scraper
 * destroyed from the `images` column.
 *
 * Returns the refs that could NOT be placed inside the question, so the caller
 * renders exactly those — the config may have fewer usable tags than the
 * `images` column has entries, and the surplus still has to appear somewhere.
 */
export function preprocessConfig(
  config: Record<string, unknown>,
  images: unknown,
): { config: Record<string, unknown>; leftoverRefs: ImageRef[] } {
  const refs = normalizeImages(images);
  try {
    // Only image-kind refs can go in a tag; html/spec/unresolved refs have no
    // URL and always belong in the leftovers.
    const placeable = refs.filter(
      (r): r is Extract<ImageRef, { kind: "image" }> =>
        r.kind === "image" && Boolean(r.candidates[0]),
    );
    const fallbacks = placeable.map((r) => ({ url: r.candidates[0]!, alt: r.alt }));

    const out = resolveImageSrcsInJson(JSON.stringify(config), fallbacks);

    // A ref is a leftover only if it is not already showing inside the question.
    // Counting substitutions instead missed refs the database had already
    // repaired, so nothing was substituted and every figure drew a second time
    // above the survey.
    //
    // Matched on the whole `src="…"` attribute, not as a bare substring: a
    // candidate URL is a prefix of every longer URL sharing its path, so
    // `…/ka/abc.svg` would otherwise count itself as placed by `…/ka/abc.svg2`.
    //
    // One occurrence is consumed per ref rather than testing presence. 18 rows
    // list the same figure twice; a presence test marked both as placed off a
    // single tag, so the second figure was neither in the question nor in the
    // leftovers and disappeared entirely.
    const unclaimed = new Map<string, number>();
    const occurrences = (c: string) => out.split(`src=\\"${c}\\"`).length - 1;

    const placed = new Set<ImageRef>();
    for (const ref of placeable) {
      for (const c of ref.candidates) {
        if (!unclaimed.has(c)) unclaimed.set(c, occurrences(c));
        const left = unclaimed.get(c) ?? 0;
        if (left > 0) {
          unclaimed.set(c, left - 1);
          placed.add(ref);
          break;
        }
      }
    }

    return {
      config: JSON.parse(out) as Record<string, unknown>,
      leftoverRefs: refs.filter((r) => !placed.has(r)),
    };
  } catch (err) {
    // Never throw: a bad substitution would take the whole dialog down. Nothing
    // was placed, so everything is a leftover.
    console.error("[QuestionBank] surveyjs_config preprocessing failed", err);
    return { config, leftoverRefs: refs };
  }
}

/**
 * True when explanation_steps and hint_text hold the same content. The Khan
 * scrape writes an identical array to both for 14,762 of its 14,834 rows, so the
 * preview showed one list twice. Compared element-wise: these are two separately
 * parsed arrays, never the same reference.
 */
export function sameSteps(
  a: string[] | null | undefined,
  b: string[] | null | undefined,
): boolean {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((s, i) => String(s).trim() === String(b[i]).trim());
}
